<svelte:options runes={false} />
<script>
  // The decision tape (TASK-20260728-researcher-observability-ui): ONE shared time axis, five stacked lanes —
  // NAV context, regime band, decisions (glyph markers), alerts (severity ticks), steering (operator ticks) —
  // so every decision reads against equity, market state, health, and human intervention at the same instant.
  // Hand-rolled Canvas 2D per the repo's zero-chart-dependency idiom (chartUtils/CandleChart pattern), but on
  // a TIME x-scale (lanes sample at different cadences, so index-scale would shear them out of alignment).
  //
  // Identity is never color-alone: decision types carry distinct GLYPHS (▲ entry / ▼ exit / ◆ framework close /
  // ▪ book snapshot), exits are additionally labeled by rule id in the hover readout/inspector, and the HTML
  // legend above the canvas names every mark. Keyboard: ←/→ step through decision events, Enter opens the
  // inspector (roving the same 'select' event as click).
  import { onMount, createEventDispatcher } from 'svelte';
  import { COLORS } from './chartUtils.js';
  import { decisionMeta, exitRuleMeta, timeDomain, xForTime } from '../../../lib/lineage.js';

  export let navPoints = [];      // [{ time, value }] ascending — the equity context lane
  export let regimePoints = [];   // [{ time, value }] ascending — regime score 0..1 (may be empty)
  export let decisions = [];      // DecisionRow[] (any order)
  export let alerts = [];         // [{ time, severity, code }] (any order)
  export let steering = [];       // merged steering feed [{ time, action, actor, source }] (any order)
  export let selectedId = null;   // the selected decision's id (highlight)

  const dispatch = createEventDispatcher();

  let canvas;
  let ctx;
  let container;
  let width = 800;
  let hoverX = -1;
  let hoverEvent = null; // { kind, item, x, laneY }
  let keyboardIndex = -1; // roving index over decisionEvents (keyboard a11y)

  // ---- Lane geometry (top → bottom). Left gutter carries the lane labels.
  const GUTTER = 74;
  const RIGHT = 16;
  const LANES = [
    { key: 'nav', label: 'NAV', h: 92 },
    { key: 'regime', label: 'Regime', h: 64 },
    { key: 'decisions', label: 'Decisions', h: 48 },
    { key: 'alerts', label: 'Alerts', h: 30 },
    { key: 'steering', label: 'Steering', h: 30 },
  ];
  const AXIS_H = 24;
  const height = LANES.reduce((s, l) => s + l.h, 0) + AXIS_H;
  const laneTop = (key) => {
    let y = 0;
    for (const l of LANES) {
      if (l.key === key) return y;
      y += l.h;
    }
    return 0;
  };

  const SEVERITY_COLOR = { Critical: '#e0533a', Error: '#e0533a', Warning: '#d8a000', Info: '#8a8b8d' };

  $: sortedDecisions = [...(decisions ?? [])].sort((a, b) => Date.parse(a.barTime) - Date.parse(b.barTime));
  // The tape's clickable decision events (chronological — the keyboard roams these). `ts` is precomputed
  // once — the hover hit-test runs per mousemove and must not re-Date.parse thousands of strings each move.
  $: decisionEvents = sortedDecisions.map((d) => ({ kind: 'decision', time: d.barTime, ts: Date.parse(d.barTime), item: d }));
  $: alertEvents = (alerts ?? []).map((a) => ({ kind: 'alert', time: a.time, ts: Date.parse(a.time), item: a }));
  $: steeringEvents = (steering ?? []).map((s) => ({ kind: 'steering', time: s.time, ts: Date.parse(s.time), item: s }));

  $: domain = timeDomain([navPoints, regimePoints, decisionEvents, alertEvents, steeringEvents]);
  // Any event or context series makes the tape drawable — a single alert/steering event (degenerate
  // one-instant domain) still renders centered, never "Nothing on the tape".
  $: hasData = decisionEvents.length > 0 || alertEvents.length > 0 || steeringEvents.length > 0
    || (navPoints?.length ?? 0) > 0 || (regimePoints?.length ?? 0) > 0;
  $: plotW = Math.max(50, width - GUTTER - RIGHT);
  const xOf = (t, dom, w) => xForTime(Date.parse(t), dom, GUTTER, w);

  function laneLabel(l, y) {
    ctx.fillStyle = COLORS.label;
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(l.label.toUpperCase(), 8, y + 14);
  }

  function drawSeriesLane(points, lane, color, { fillBand = null } = {}) {
    const top = laneTop(lane) + 8;
    const h = LANES.find((l) => l.key === lane).h - 16;
    const ys = (points ?? []).map((p) => Number(p.value)).filter(Number.isFinite);
    if (ys.length === 0) {
      ctx.fillStyle = COLORS.label;
      ctx.globalAlpha = 0.55;
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('no data in window', GUTTER + 6, top + h / 2 + 3);
      ctx.globalAlpha = 1;
      return null;
    }
    let lo = Math.min(...ys);
    let hi = Math.max(...ys);
    if (fillBand) { lo = Math.min(lo, fillBand.lo); hi = Math.max(hi, fillBand.hi); }
    if (lo === hi) { lo -= 1; hi += 1; }
    const yOf = (v) => top + (1 - (v - lo) / (hi - lo)) * h;

    // Threshold band (regime lane): shade the entry/exit gates so "where the strategy is being steered"
    // is visible at a glance. Labels sit in the gutter (not color-alone).
    if (fillBand) {
      for (const th of fillBand.lines) {
        const y = yOf(th.v);
        ctx.strokeStyle = th.color;
        ctx.globalAlpha = 0.55;
        ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(GUTTER, y); ctx.lineTo(width - RIGHT, y); ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
        ctx.fillStyle = COLORS.label;
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(th.label, 8, y + 3);
      }
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    let started = false;
    for (const p of points) {
      const v = Number(p.value);
      if (!Number.isFinite(v)) continue;
      const x = xOf(p.time, domain, plotW);
      if (!started) { ctx.moveTo(x, yOf(v)); started = true; }
      else ctx.lineTo(x, yOf(v));
    }
    ctx.stroke();
    return yOf;
  }

  function drawDecisionMarker(d, x, centerY, isSelected) {
    const meta = decisionMeta(d.decisionType);
    const color = d.decisionType === 'exit' ? exitRuleMeta(d.exitRule).color : meta.color;
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    if (isSelected) {
      ctx.strokeStyle = '#e8e9ea';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, centerY, 9, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = color;
    }
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (d.decisionType === 'entry') {
      ctx.moveTo(x, centerY - 5); ctx.lineTo(x - 4.5, centerY + 4); ctx.lineTo(x + 4.5, centerY + 4);
      ctx.closePath(); ctx.fill();
    } else if (d.decisionType === 'exit') {
      ctx.moveTo(x, centerY + 5); ctx.lineTo(x - 4.5, centerY - 4); ctx.lineTo(x + 4.5, centerY - 4);
      ctx.closePath(); ctx.fill();
    } else if (d.decisionType === 'external_close') {
      ctx.moveTo(x, centerY - 5.5); ctx.lineTo(x + 5, centerY); ctx.lineTo(x, centerY + 5.5); ctx.lineTo(x - 5, centerY);
      ctx.closePath(); ctx.fill();
    } else {
      // book_snapshot — a small square at the lane floor (a heartbeat, not a trade).
      ctx.globalAlpha = 0.8;
      ctx.fillRect(x - 2, centerY + 8, 4, 4);
      ctx.globalAlpha = 1;
    }
  }

  function drawTicks(events, lane, colorOf) {
    const top = laneTop(lane) + 7;
    const h = LANES.find((l) => l.key === lane).h - 14;
    for (const e of events) {
      const x = xOf(e.time, domain, plotW);
      ctx.strokeStyle = colorOf(e.item);
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, top + h); ctx.stroke();
    }
  }

  function drawTimeAxis() {
    const y = height - 7;
    ctx.fillStyle = COLORS.label;
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    const span = domain.max - domain.min;
    if (span <= 0) return;
    const ticks = 6;
    for (let i = 0; i <= ticks; i++) {
      const t = domain.min + (span * i) / ticks;
      const x = GUTTER + (plotW * i) / ticks;
      const d = new Date(t);
      const label = span > 3 * 86400_000
        ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      ctx.fillText(label, x, y);
    }
  }

  function draw() {
    if (!ctx || !hasData) return;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, width, height);

    // Lane separators + labels.
    let y = 0;
    for (const l of LANES) {
      laneLabel(l, y);
      y += l.h;
      ctx.strokeStyle = COLORS.grid;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
    }

    drawSeriesLane(navPoints, 'nav', COLORS.line);
    drawSeriesLane(regimePoints, 'regime', '#b58af5', {
      fillBand: {
        lo: 0, hi: 1,
        lines: [
          { v: 0.45, label: '0.45 entry', color: '#1aa260' },
          { v: 0.15, label: '0.15 exit', color: '#e0533a' },
        ],
      },
    });

    const decCenter = laneTop('decisions') + LANES.find((l) => l.key === 'decisions').h / 2 - 4;
    for (const e of decisionEvents) {
      drawDecisionMarker(e.item, xOf(e.time, domain, plotW), decCenter, e.item.id === selectedId);
    }

    drawTicks(alertEvents, 'alerts', (a) => SEVERITY_COLOR[a.severity] ?? '#8a8b8d');
    drawTicks(steeringEvents, 'steering', () => '#3c78f0');
    drawTimeAxis();

    // Crosshair.
    if (hoverX >= GUTTER) {
      ctx.strokeStyle = COLORS.crosshair;
      ctx.globalAlpha = 0.4;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(hoverX, 0); ctx.lineTo(hoverX, height - AXIS_H); ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
  }

  // Nearest event across the three event lanes within a horizontal hit radius, preferring the hovered lane.
  function hitTest(mx, my) {
    const RADIUS = 8;
    const laneOf = (yy) => {
      let acc = 0;
      for (const l of LANES) {
        if (yy >= acc && yy < acc + l.h) return l.key;
        acc += l.h;
      }
      return null;
    };
    const hoveredLane = laneOf(my);
    const pools = [
      ['decisions', decisionEvents],
      ['alerts', alertEvents],
      ['steering', steeringEvents],
    ];
    let best = null;
    for (const [lane, events] of pools) {
      for (const e of events) {
        const x = xForTime(e.ts, domain, GUTTER, plotW);
        const dx = Math.abs(x - mx);
        if (dx > RADIUS) continue;
        const lanePreference = lane === hoveredLane ? 0 : 1;
        const score = lanePreference * 100 + dx;
        if (!best || score < best.score) best = { ...e, x, score };
      }
    }
    return best;
  }

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hoverX = e.clientX - rect.left;
    hoverEvent = hitTest(hoverX, e.clientY - rect.top);
  }
  function onLeave() { hoverX = -1; hoverEvent = null; }
  function onClick() {
    if (hoverEvent) dispatch('select', { kind: hoverEvent.kind, item: hoverEvent.item });
  }
  function onKeydown(e) {
    if (decisionEvents.length === 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      keyboardIndex = e.key === 'ArrowRight'
        ? Math.min(decisionEvents.length - 1, keyboardIndex + 1)
        : Math.max(0, keyboardIndex <= 0 ? 0 : keyboardIndex - 1);
      const ev = decisionEvents[keyboardIndex];
      hoverX = xOf(ev.time, domain, plotW);
      hoverEvent = { ...ev, x: hoverX };
    } else if (e.key === 'Enter' && keyboardIndex >= 0) {
      e.preventDefault();
      dispatch('select', { kind: 'decision', item: decisionEvents[keyboardIndex].item });
    }
  }

  function hoverText(ev) {
    if (!ev) return '';
    const when = new Date(ev.time).toLocaleString();
    if (ev.kind === 'decision') {
      const d = ev.item;
      const meta = decisionMeta(d.decisionType);
      const rule = d.decisionType === 'exit' || d.decisionType === 'external_close' ? exitRuleMeta(d.exitRule) : null;
      const sym = d.symbol ? ` ${d.symbol.replace('_', '-')}` : '';
      return `${meta.glyph} ${meta.label}${sym} · ${when}${rule && rule.id !== null ? ` · EXIT ${rule.id} ${rule.label}` : ''}`;
    }
    if (ev.kind === 'alert') return `⚠ ${ev.item.severity} ${ev.item.code} · ${when}`;
    return `⚙ ${ev.item.action}${ev.item.actor ? ` by ${ev.item.actor}` : ''} · ${when}`;
  }

  $: if (ctx && canvas && hasData && width && (hoverX || true) && domain && navPoints && regimePoints && decisions && alerts && steering && selectedId !== undefined) draw();
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  function resize() {
    if (!container) return;
    width = Math.max(320, container.offsetWidth);
  }

  onMount(() => {
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    // Container-width changes without a window resize (sidebar toggle, panel expansion) would leave the
    // bitmap width stale — CSS then stretches it and the hover hit-test drifts. Observe the container.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro && container) ro.observe(container);
    return () => { window.removeEventListener('resize', onResize); ro?.disconnect(); };
  });
</script>

<div class="tape" bind:this={container}>
  <!-- Legend: names every mark (identity never color-alone). -->
  <div class="tape-legend" aria-hidden="false">
    <span class="tl-item"><span class="tl-glyph" style="color:#1aa260">▲</span> Entry</span>
    <span class="tl-item"><span class="tl-glyph" style="color:#e0533a">▼</span> Exit (colored by rule)</span>
    <span class="tl-item"><span class="tl-glyph" style="color:#d8a000">◆</span> Framework close</span>
    <span class="tl-item"><span class="tl-glyph" style="color:#8a8b8d">▪</span> Book snapshot</span>
    <span class="tl-item"><span class="tl-glyph" style="color:#e0533a">│</span> Alert</span>
    <span class="tl-item"><span class="tl-glyph" style="color:#3c78f0">│</span> Steering</span>
  </div>

  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      role="application"
      aria-roledescription="interactive decision timeline"
      aria-label={`Decision tape: ${decisionEvents.length} decisions, ${alertEvents.length} alerts, ${steeringEvents.length} steering events on one timeline. Use arrow keys to step through decisions and Enter to inspect.`}
      style={`cursor: ${hoverEvent ? 'pointer' : 'crosshair'}`}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:click={onClick}
      on:keydown={onKeydown}></canvas>
    <div class="tape-hover" aria-live="polite">
      {#if hoverEvent}{hoverText(hoverEvent)} — click to inspect{:else}&nbsp;{/if}
    </div>
  {:else}
    <div class="empty-state">
      <p class="empty-state-description">Nothing on the tape for this window yet.</p>
    </div>
  {/if}
</div>

<style>
  .tape { width: 100%; }
  canvas { display: block; width: 100%; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; }
  canvas:focus-visible { outline: 2px solid var(--accent, #3c78f0); outline-offset: 1px; }
  .tape-legend {
    display: flex; flex-wrap: wrap; gap: 0.9rem;
    font-size: 0.72rem; color: var(--text-muted, #8a8b8d);
    padding: 0.25rem 0 0.45rem;
  }
  .tl-item { display: inline-flex; align-items: center; gap: 0.3rem; }
  .tl-glyph { font-size: 0.8rem; line-height: 1; }
  .tape-hover {
    min-height: 1.2rem; font-size: 0.75rem; color: var(--text-secondary, #b5b6b8);
    padding: 0.3rem 0; font-variant-numeric: tabular-nums;
  }
</style>
